import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { portalAPI } from '../api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter 
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Shield, Plus, FileText, Calendar, AlertCircle, CheckCircle, 
  Clock, Package, Upload, ChevronRight, Info, Loader2
} from 'lucide-react';

const CustomerPortalWarrantiesPage = () => {
  const { user } = useAuth();
  const [warranties, setWarranties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const [selectedWarranty, setSelectedWarranty] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [claimForm, setClaimForm] = useState({
    issue_description: '',
    preferred_resolution: 'repair',
    attachments: []
  });
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [registerForm, setRegisterForm] = useState({
    product_name: '',
    serial_number: '',
    pump_serial_number: '',
    oil_water_filtration_serial_number: '',
    purchase_date: '',
    shipping_date: '',
    place_of_purchase: '',
    proof_of_purchase_url: '',
    request_extended_warranty: false,
    phone: '',
    email: '',
    address: '',
    city: '',
    province_state: '',
    country: ''
  });

  const isWithin30Days = (dateStr) => {
    if (!dateStr) return false;
    const diff = (Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24);
    return diff >= 0 && diff <= 30;
  };

  const handleProofUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Proof of purchase exceeds 10MB limit');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setRegisterForm(prev => ({ ...prev, proof_of_purchase_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const openRegisterDialog = () => {
    setRegisterForm({
      product_name: '',
      serial_number: '',
      pump_serial_number: '',
      oil_water_filtration_serial_number: '',
      purchase_date: '',
      shipping_date: '',
      place_of_purchase: '',
      proof_of_purchase_url: '',
      request_extended_warranty: false,
      phone: user?.phone || '',
      email: user?.email || '',
      address: '',
      city: '',
      province_state: '',
      country: ''
    });
    setRegisterDialogOpen(true);
  };

  const handleRegisterWarranty = async () => {
    const f = registerForm;
    if (!f.product_name.trim() || !f.serial_number.trim() || !f.purchase_date) {
      toast.error('Please provide product, serial number, and purchase date');
      return;
    }
    if (!f.pump_serial_number.trim()) {
      toast.error('Pump serial number is required');
      return;
    }
    if (!f.shipping_date) {
      toast.error('Shipping date is required');
      return;
    }
    if (!f.phone.trim()) {
      toast.error('Phone number is required');
      return;
    }
    if (!f.email.trim() || !f.address.trim() || !f.city.trim() || !f.province_state.trim() || !f.country.trim()) {
      toast.error('Please provide your full address (email, street, city, province/state, country)');
      return;
    }
    if (!f.proof_of_purchase_url) {
      toast.error('Proof of purchase is required');
      return;
    }
    setRegistering(true);
    try {
      await portalAPI.registerWarranty(registerForm);
      toast.success('Product registered! Coverage is pending verification by our team.');
      setRegisterDialogOpen(false);
      fetchWarranties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register product');
    } finally {
      setRegistering(false);
    }
  };

  useEffect(() => {
    fetchWarranties();
  }, []);

  const fetchWarranties = async () => {
    try {
      const res = await portalAPI.getWarranties();
      setWarranties(res.data || []);
    } catch (error) {
      console.error('Failed to fetch warranties:', error);
      // Show empty state instead of error
    } finally {
      setLoading(false);
    }
  };

  const openClaimDialog = (warranty) => {
    setSelectedWarranty(warranty);
    setClaimForm({
      issue_description: '',
      preferred_resolution: 'repair',
      attachments: []
    });
    setClaimDialogOpen(true);
  };

  const handleSubmitClaim = async () => {
    if (!claimForm.issue_description.trim()) {
      toast.error('Please describe the issue');
      return;
    }

    setSubmitting(true);
    try {
      await portalAPI.submitWarrantyClaim({
        warranty_id: selectedWarranty.id,
        ...claimForm
      });
      toast.success('Warranty claim submitted successfully! We will review it shortly.');
      setClaimDialogOpen(false);
      fetchWarranties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit claim');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-100 text-emerald-700',
      pending: 'bg-amber-100 text-amber-700',
      expired: 'bg-slate-100 text-slate-600',
      claimed: 'bg-amber-100 text-amber-700',
      void: 'bg-red-100 text-red-700'
    };
    const labels = { pending: 'Pending Approval' };
    return <Badge className={styles[status] || styles.active}>{labels[status] || status}</Badge>;
  };

  const getClaimStatusBadge = (status) => {
    const styles = {
      pending: { bg: 'bg-amber-100 text-amber-700', icon: Clock },
      approved: { bg: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
      rejected: { bg: 'bg-red-100 text-red-700', icon: AlertCircle },
      in_progress: { bg: 'bg-blue-100 text-blue-700', icon: Loader2 },
      resolved: { bg: 'bg-green-100 text-green-700', icon: CheckCircle }
    };
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    return (
      <Badge className={style.bg}>
        <Icon className="h-3 w-3 mr-1" />
        {status?.replace('_', ' ')}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-blue-600" />
            My Warranty Claims
          </h1>
          <p className="text-muted-foreground mt-1">
            Register your equipment and submit warranty claims
          </p>
        </div>
        <Button onClick={openRegisterDialog} data-testid="register-product-btn">
          <Plus className="h-4 w-4 mr-2" />
          Register a Product
        </Button>
      </div>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="pt-4">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-blue-900">How Warranty Claims Work</p>
              <ul className="mt-2 space-y-1 text-blue-700">
                <li>1. Find your product warranty below</li>
                <li>2. Click &quot;File a Claim&quot; to report an issue</li>
                <li>3. Describe the problem and preferred resolution</li>
                <li>4. Our team will review and respond within 2-3 business days</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Warranties List */}
      {warranties.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Shield className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900">No Warranties Found</h3>
            <p className="text-muted-foreground mt-1 mb-4">
              You haven&apos;t registered any equipment yet.
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              Register your freeze dryer when you receive it to activate warranty coverage.
            </p>
            <Button onClick={openRegisterDialog} data-testid="register-product-empty-btn">
              <Plus className="h-4 w-4 mr-2" />
              Register a Product
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {warranties.map((warranty) => (
            <Card key={warranty.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex gap-4">
                    <div className="p-3 bg-slate-100 rounded-lg">
                      <Package className="h-6 w-6 text-slate-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{warranty.product_name}</h3>
                      <p className="text-sm text-muted-foreground">
                        Serial: {warranty.serial_number || 'N/A'}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          Purchased: {format(new Date(warranty.purchase_date), 'MMM d, yyyy')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          Expires: {format(new Date(warranty.expiry_date), 'MMM d, yyyy')}
                        </span>
                      </div>
                      {/* Component coverage breakdown */}
                      {warranty.coverage && (
                        <div className="mt-3 flex flex-wrap gap-2" data-testid={`coverage-${warranty.id}`}>
                          <Badge variant="outline" className="text-xs font-normal">
                            Structure → {format(new Date(warranty.coverage.structure), 'MMM yyyy')}
                          </Badge>
                          <Badge variant="outline" className="text-xs font-normal">
                            Electronics & Vacuum Pump → {format(new Date(warranty.coverage.electronics_vacuum_pump), 'MMM yyyy')}
                          </Badge>
                          <Badge variant="outline" className="text-xs font-normal">
                            Cooling → {format(new Date(warranty.coverage.cooling), 'MMM yyyy')}
                          </Badge>
                          {warranty.extended_warranty_requested && (
                            <Badge className="text-xs bg-indigo-100 text-indigo-700">Extended warranty requested</Badge>
                          )}
                        </div>
                      )}
                      {warranty.status === 'pending' && (
                        <p className="mt-2 text-xs text-amber-600 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Pending verification by our team. You can file a claim once approved.
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    {getStatusBadge(warranty.status)}
                    {warranty.status === 'active' && (
                      <Button 
                        className="mt-3"
                        onClick={() => openClaimDialog(warranty)}
                        data-testid={`file-claim-${warranty.id}`}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        File a Claim
                      </Button>
                    )}
                  </div>
                </div>

                {/* Existing Claims */}
                {warranty.claims && warranty.claims.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="text-sm font-medium mb-2">Claim History</h4>
                    <div className="space-y-2">
                      {warranty.claims.map((claim, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-slate-50 p-3 rounded-lg">
                          <div>
                            <p className="text-sm font-medium">Claim #{claim.claim_number || idx + 1}</p>
                            <p className="text-xs text-muted-foreground">
                              Filed: {format(new Date(claim.created_at), 'MMM d, yyyy')}
                            </p>
                          </div>
                          {getClaimStatusBadge(claim.status)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* File Claim Dialog */}
      <Dialog open={claimDialogOpen} onOpenChange={setClaimDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              File Warranty Claim
            </DialogTitle>
            <DialogDescription>
              {selectedWarranty?.product_name} - Serial: {selectedWarranty?.serial_number || 'N/A'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="issue">Describe the Issue *</Label>
              <Textarea
                id="issue"
                placeholder="Please describe what went wrong with the product..."
                value={claimForm.issue_description}
                onChange={(e) => setClaimForm(prev => ({ ...prev, issue_description: e.target.value }))}
                className="mt-1"
                rows={4}
              />
            </div>

            <div>
              <Label htmlFor="resolution">Preferred Resolution</Label>
              <Select
                value={claimForm.preferred_resolution}
                onValueChange={(v) => setClaimForm(prev => ({ ...prev, preferred_resolution: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="repair">Repair</SelectItem>
                  <SelectItem value="replacement">Replacement</SelectItem>
                  <SelectItem value="refund">Refund</SelectItem>
                  <SelectItem value="other">Other (specify in description)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
              <p className="font-medium text-amber-800">What happens next?</p>
              <ul className="mt-1 text-amber-700 space-y-1">
                <li>• Our team will review your claim within 2-3 business days</li>
                <li>• You&apos;ll receive an email notification with the decision</li>
                <li>• If approved, we&apos;ll provide instructions for the next steps</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setClaimDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmitClaim} disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Claim'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Register Product Dialog */}
      <Dialog open={registerDialogOpen} onOpenChange={setRegisterDialogOpen}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Register Your Product
            </DialogTitle>
            <DialogDescription>
              Register your freeze dryer to activate warranty coverage. Our team will verify your registration.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="product_name">Product / Model *</Label>
              <Input
                id="product_name"
                placeholder="e.g., Chinook Freeze Dryer Model X"
                value={registerForm.product_name}
                onChange={(e) => setRegisterForm(prev => ({ ...prev, product_name: e.target.value }))}
                className="mt-1"
                data-testid="register-product-name"
              />
            </div>
            <div>
              <Label htmlFor="serial_number">Serial Number *</Label>
              <Input
                id="serial_number"
                placeholder="Found on the unit's nameplate"
                value={registerForm.serial_number}
                onChange={(e) => setRegisterForm(prev => ({ ...prev, serial_number: e.target.value }))}
                className="mt-1"
                data-testid="register-serial-number"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="pump_serial">Pump Serial Number *</Label>
                <Input
                  id="pump_serial"
                  placeholder="Vacuum pump serial"
                  value={registerForm.pump_serial_number}
                  onChange={(e) => setRegisterForm(prev => ({ ...prev, pump_serial_number: e.target.value }))}
                  className="mt-1"
                  data-testid="register-pump-serial"
                />
              </div>
              <div>
                <Label htmlFor="owf_serial">Oil/Water Filtration Serial</Label>
                <Input
                  id="owf_serial"
                  placeholder="If purchased"
                  value={registerForm.oil_water_filtration_serial_number}
                  onChange={(e) => setRegisterForm(prev => ({ ...prev, oil_water_filtration_serial_number: e.target.value }))}
                  className="mt-1"
                  data-testid="register-owf-serial"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="purchase_date">Purchase Date *</Label>
                <Input
                  id="purchase_date"
                  type="date"
                  max={new Date().toISOString().split('T')[0]}
                  value={registerForm.purchase_date}
                  onChange={(e) => setRegisterForm(prev => ({ ...prev, purchase_date: e.target.value }))}
                  className="mt-1"
                  data-testid="register-purchase-date"
                />
              </div>
              <div>
                <Label htmlFor="shipping_date">Shipping Date *</Label>
                <Input
                  id="shipping_date"
                  type="date"
                  value={registerForm.shipping_date}
                  onChange={(e) => setRegisterForm(prev => ({ ...prev, shipping_date: e.target.value }))}
                  className="mt-1"
                  data-testid="register-shipping-date"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="place_of_purchase">Where Purchased</Label>
              <Input
                id="place_of_purchase"
                placeholder="Dealer, website, or store name"
                value={registerForm.place_of_purchase}
                onChange={(e) => setRegisterForm(prev => ({ ...prev, place_of_purchase: e.target.value }))}
                className="mt-1"
                data-testid="register-place"
              />
            </div>

            {/* Contact & shipping details */}
            <div className="pt-2 border-t">
              <p className="text-sm font-medium text-slate-700 mb-2">Your Contact &amp; Shipping Details</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="reg_email">Email *</Label>
                  <Input id="reg_email" type="email" value={registerForm.email}
                    onChange={(e) => setRegisterForm(prev => ({ ...prev, email: e.target.value }))}
                    className="mt-1" data-testid="register-email" />
                </div>
                <div>
                  <Label htmlFor="reg_phone">Phone *</Label>
                  <Input id="reg_phone" value={registerForm.phone}
                    onChange={(e) => setRegisterForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="mt-1" data-testid="register-phone" />
                </div>
              </div>
              <div className="mt-3">
                <Label htmlFor="reg_address">Address *</Label>
                <Input id="reg_address" placeholder="Street address" value={registerForm.address}
                  onChange={(e) => setRegisterForm(prev => ({ ...prev, address: e.target.value }))}
                  className="mt-1" data-testid="register-address" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                <div>
                  <Label htmlFor="reg_city">City *</Label>
                  <Input id="reg_city" value={registerForm.city}
                    onChange={(e) => setRegisterForm(prev => ({ ...prev, city: e.target.value }))}
                    className="mt-1" data-testid="register-city" />
                </div>
                <div>
                  <Label htmlFor="reg_state">Province / State *</Label>
                  <Input id="reg_state" value={registerForm.province_state}
                    onChange={(e) => setRegisterForm(prev => ({ ...prev, province_state: e.target.value }))}
                    className="mt-1" data-testid="register-province-state" />
                </div>
                <div>
                  <Label htmlFor="reg_country">Country *</Label>
                  <Input id="reg_country" value={registerForm.country}
                    onChange={(e) => setRegisterForm(prev => ({ ...prev, country: e.target.value }))}
                    className="mt-1" data-testid="register-country" />
                </div>
              </div>
            </div>
            <div>
              <Label htmlFor="proof">Proof of Purchase *</Label>
              <Input
                id="proof"
                type="file"
                accept="image/*,application/pdf"
                onChange={handleProofUpload}
                className="mt-1"
                data-testid="register-proof"
              />
              {registerForm.proof_of_purchase_url && (
                <p className="text-xs text-emerald-600 mt-1">Proof attached</p>
              )}
            </div>

            {/* Coverage info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
              <p className="font-medium text-blue-900">Standard Warranty Coverage (included)</p>
              <ul className="mt-1 text-blue-700 space-y-0.5">
                <li>• Structure: 2 years</li>
                <li>• Electronics &amp; vacuum pump: 1 year</li>
                <li>• Cooling system: 3 years</li>
              </ul>
            </div>

            {/* Extended warranty (paid) */}
            <div className={`flex items-start gap-2 rounded-lg border p-3 ${isWithin30Days(registerForm.purchase_date) ? 'border-indigo-200 bg-indigo-50' : 'border-slate-200 bg-slate-50 opacity-70'}`}>
              <Checkbox
                id="extended"
                checked={registerForm.request_extended_warranty}
                disabled={!isWithin30Days(registerForm.purchase_date)}
                onCheckedChange={(checked) => setRegisterForm(prev => ({ ...prev, request_extended_warranty: !!checked }))}
                data-testid="register-extended-warranty"
              />
              <Label htmlFor="extended" className="text-sm leading-snug cursor-pointer">
                <span className="flex items-center gap-2 font-medium">
                  Add Extended Warranty
                  <Badge className="bg-indigo-100 text-indigo-700">Paid upgrade</Badge>
                </span>
                <span className="block text-xs text-muted-foreground mt-1">
                  Extends Structure to <strong>3 years</strong> and Electrical (electronics &amp; vacuum pump) to <strong>2 years</strong>.
                  {isWithin30Days(registerForm.purchase_date)
                    ? ' Our team will contact you with pricing to complete the purchase.'
                    : ' Only available within 30 days of purchase — enter a recent purchase date to enable.'}
                </span>
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRegisterDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRegisterWarranty} disabled={registering} data-testid="submit-register-btn">
              {registering ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Registering...
                </>
              ) : (
                'Register Product'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerPortalWarrantiesPage;
